import React, { useMemo, useState } from 'react';
import { Col, Row } from 'antd';
import { Input } from 'formik-antd';
import { FormikProps } from 'formik';

import FormItem from 'components/formik/FormItem';
import FormikSelectWithLoading from 'hocs/withLoading/FormikSelectWithLoading';
import FormikAutoCompleteWithLoading from 'hocs/withLoading/FormikAutoCompleteWithLoading';
import { useAppSelector } from 'hooks/useAppSelector';
import {
  countriesSelector,
  countryCitiesSelector,
  isCountriesLoadingSelector,
  isCountryCitiesLoadingSelector,
} from 'redux/selectors/regionSelectors';
import { GeneralInfoFormValues } from '../GeneralInfo';

type Props = {
  formik: FormikProps<GeneralInfoFormValues>;
};

export default function InternationalCompanyStateForm(props: Props) {
  const { formik } = props;
  const [selectedCountry, setSelectedCountry] = useState(formik.values.country);
  const countries = useAppSelector(countriesSelector);
  const isCountriesLoading = useAppSelector(isCountriesLoadingSelector);
  const cities = useAppSelector(countryCitiesSelector);
  const isCountryCitiesLoading = useAppSelector(isCountryCitiesLoadingSelector);
  const filteredCities = useMemo(
    () => cities.filter((city) => city.countryId === selectedCountry),
    [selectedCountry, cities],
  );

  const handleCountryChange = (
    countryId: number,
    formik: FormikProps<GeneralInfoFormValues>,
  ) => {
    setSelectedCountry(countryId);

    formik.setFieldValue('city', '', false);
    formik.setFieldValue('state', undefined, false);
    formik.setFieldValue('internationalCityName', '', false);
    formik.setFieldValue('internationalStateName', '', false);
  };

  return (
    <Row gutter={40}>
      <Col xs={24} md={12}>
        <Row gutter={20}>
          <Col xs={24} md={12}>
            <FormItem
              name="country"
              label="Country *"
              data-id="companyCountryField"
            >
              <FormikSelectWithLoading
                isLoading={isCountriesLoading}
                name="country"
                showSearch
                placeholder="Select country"
                optionFilterProp="children"
                onChange={(value: number) => handleCountryChange(value, formik)}
                value={selectedCountry}
              >
                {countries.map((country) => (
                  <FormikSelectWithLoading.Option
                    key={`select-country-${country.id}`}
                    value={country.id || -1}
                  >
                    {country.name}
                  </FormikSelectWithLoading.Option>
                ))}
              </FormikSelectWithLoading>
            </FormItem>
          </Col>

          <Col xs={24} md={12}>
            <FormItem
              name="internationalStateName"
              label="State *"
              data-id="companyStateField"
            >
              <Input
                name="internationalStateName"
                placeholder="Enter state"
                disabled={!formik.values.country}
                autoComplete="off"
              />
            </FormItem>
          </Col>

          <Col xs={24} md={12}>
            <FormItem
              data-id="companyCityField"
              name="internationalCityName"
              label="City *"
            >
              <FormikAutoCompleteWithLoading
                isLoading={isCountryCitiesLoading}
                name="internationalCityName"
                filterOption
                disabled={!formik.values.internationalStateName}
                placeholder="Enter city"
                showSearch
              >
                {filteredCities.map((city) => (
                  <FormikAutoCompleteWithLoading.Option
                    key={`city-state-${city.id}`}
                    value={city.name || ''}
                  >
                    {city.name}
                  </FormikAutoCompleteWithLoading.Option>
                ))}
              </FormikAutoCompleteWithLoading>
            </FormItem>
          </Col>
        </Row>
      </Col>

      <Col xs={24} md={12}>
        <Row gutter={20}>
          <Col xs={24} md={12}>
            <FormItem
              name="mainAddress"
              label="Main address *"
              data-id="newClientMainAddressField"
            >
              <Input
                name="mainAddress"
                placeholder="Enter head-office address"
                autoComplete="off"
              />
            </FormItem>
          </Col>

          <Col xs={24} md={12}>
            <FormItem label="ZIP code" data-id="newClientZipField" name="zip">
              <Input
                name="zip"
                className="full-width"
                placeholder="Enter ZIP code"
                autoComplete="off"
              />
            </FormItem>
          </Col>
        </Row>
      </Col>
    </Row>
  );
}
